import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ChevronRightIcon, ChevronLeftIcon, LayersIcon, SlidersHorizontalIcon, RotateCcwIcon, BookmarkIcon, XIcon } from 'lucide-react';
import { applyEditionToBook, getLinkedStoryForBook } from '../data/linkedEditions';
import { useReadingPreferences } from '../context/ReadingPreferencesContext';
import { darknessSlugToLevel, hasExcludedWarning, matchesEndingSlugs, matchesGenreSlugs, matchesLevelSlugs, matchesRelationshipSlugs, matchesTagSlugs, spiceSlugToLevel } from '../utils/filterVocabulary';
import ProductCard from '../components/Products/ProductCard';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import ViewModeToggle from '../components/UI/ViewModeToggle';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { usePlatform } from '../context/PlatformContext';
import { useAfterTideFilter } from '../hooks/useAfterTideFilter';
import { allBooks, bestSellerBooks, newReleaseBooks, debutBooks, hiddenGemBooks } from '../data/booksData';
import { aftertideBooks } from '../data/aftertideBooksData';
// Mock data for demonstration
const books = [{
  id: '1',
  title: 'The Silent Echo',
  author: 'Elena Richards',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.7,
  formats: ['paperback', 'ebook', 'audiobook'],
  genres: ['Mystery', 'Thriller'],
  isNew: true
}, {
  id: '2',
  title: 'Beyond the Veil',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.5,
  formats: ['ebook', 'audiobook'],
  genres: ['Fantasy', 'Young Adult']
}, {
  id: '3',
  title: 'Whispers in the Dark',
  author: 'Sarah Johnson',
  coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.8,
  formats: ['paperback', 'ebook'],
  genres: ['Horror', 'Supernatural']
}, {
  id: '4',
  title: 'The Lost City',
  author: 'David Williams',
  coverImage: 'https://images.unsplash.com/photo-1541963463532-d68292c34b19?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.3,
  formats: ['hardcover', 'ebook', 'audiobook'],
  genres: ['Adventure', 'Historical Fiction']
}, {
  id: '5',
  title: 'Starfall',
  author: 'Amara Knight',
  coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.9,
  formats: ['paperback', 'ebook'],
  genres: ['Sci-Fi', 'Romance'],
  isNew: true
}, {
  id: '6',
  title: 'The Hidden Truth',
  author: 'Thomas Lee',
  coverImage: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.2,
  formats: ['ebook'],
  genres: ['Mystery', 'Crime']
}, {
  id: '7',
  title: 'Echoes of Tomorrow',
  author: 'Zoe Parker',
  coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.6,
  formats: ['hardcover', 'ebook'],
  genres: ['Sci-Fi', 'Dystopian'],
  isNew: true
}, {
  id: '8',
  title: 'The Last Spell',
  author: 'Finn Murphy',
  coverImage: 'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.8,
  formats: ['paperback', 'ebook', 'audiobook'],
  genres: ['Fantasy', 'Magic'],
  isNew: true
}];
// Featured merchandise
const merchandise = [{
  id: '1',
  title: 'Silent Echo T-Shirt',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 24.99,
  relatedBook: 'The Silent Echo',
  isOfficialMerch: true
}, {
  id: '2',
  title: 'Starfall Constellation Mug',
  vendor: 'Cosmic Creations',
  image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 18.5,
  relatedBook: 'Starfall',
  isOfficialMerch: true
}, {
  id: '3',
  title: 'Beyond the Veil Bookmark Set',
  vendor: 'Page Markers',
  image: 'https://images.unsplash.com/photo-1544947987-f6c4fa25fa4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 12.99,
  relatedBook: 'Beyond the Veil'
}, {
  id: '4',
  title: 'Lost City Map Print',
  vendor: 'Adventure Art',
  image: 'https://images.unsplash.com/photo-1582574501058-6f3b5c0c5e6b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 29.99,
  relatedBook: 'The Lost City',
  isOfficialMerch: true
}, {
  id: '5',
  title: 'Whispers in the Dark Candle',
  vendor: 'Literary Scents',
  image: 'https://images.unsplash.com/photo-1603006905003-be475563f01a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 16.99,
  relatedBook: 'Whispers in the Dark',
  isOfficialMerch: true
}, {
  id: '6',
  title: 'Fantasy World Map Poster',
  vendor: 'Imaginary Cartography',
  image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 22.99,
  relatedBook: 'The Last Spell'
}];
// Mock section data — drawn from canonical catalog
const bookSections = {
  bestSellers: bestSellerBooks,
  trending: [...allBooks].sort((a, b) => (b.reviewCount ?? 0) - (a.reviewCount ?? 0)).slice(0, 8),
  newAndNoteworthy: newReleaseBooks,
  debutAuthors: debutBooks,
  readerFavorites: [...allBooks].sort((a, b) => b.rating - a.rating).slice(0, 8)
};
// Mock genre data with nested structure
const genreData = [{
  id: 'romance',
  name: 'Romance',
  subgenres: [{
    id: 'contemporary-romance',
    name: 'Contemporary Romance',
    subgenres: [{
      id: 'rom-com',
      name: 'Romantic Comedy'
    }, {
      id: 'workplace-romance',
      name: 'Workplace Romance'
    }, {
      id: 'sports-romance',
      name: 'Sports Romance'
    }]
  }, {
    id: 'historical-romance',
    name: 'Historical Romance',
    subgenres: [{
      id: 'regency',
      name: 'Regency'
    }, {
      id: 'victorian',
      name: 'Victorian'
    }, {
      id: 'medieval',
      name: 'Medieval'
    }]
  }, {
    id: 'dark-romance',
    name: 'Dark Romance',
    subgenres: [{
      id: 'mafia-romance',
      name: 'Mafia Romance'
    }, {
      id: 'bully-romance',
      name: 'Bully Romance'
    }, {
      id: 'captive-romance',
      name: 'Captive Romance'
    }]
  }, {
    id: 'paranormal-romance',
    name: 'Paranormal Romance'
  }]
}, {
  id: 'fantasy',
  name: 'Fantasy',
  subgenres: [{
    id: 'high-fantasy',
    name: 'High Fantasy',
    subgenres: [{
      id: 'epic-fantasy',
      name: 'Epic Fantasy'
    }, {
      id: 'sword-sorcery',
      name: 'Sword & Sorcery'
    }]
  }, {
    id: 'urban-fantasy',
    name: 'Urban Fantasy',
    subgenres: [{
      id: 'contemporary-fantasy',
      name: 'Contemporary Fantasy'
    }, {
      id: 'magical-realism',
      name: 'Magical Realism'
    }]
  }, {
    id: 'portal-fantasy',
    name: 'Portal Fantasy'
  }, {
    id: 'grimdark',
    name: 'Grimdark'
  }]
}, {
  id: 'sci-fi',
  name: 'Science Fiction',
  subgenres: [{
    id: 'space-opera',
    name: 'Space Opera'
  }, {
    id: 'cyberpunk',
    name: 'Cyberpunk'
  }, {
    id: 'dystopian',
    name: 'Dystopian'
  }, {
    id: 'post-apocalyptic',
    name: 'Post-Apocalyptic'
  }]
}, {
  id: 'mystery',
  name: 'Mystery',
  subgenres: [{
    id: 'cozy-mystery',
    name: 'Cozy Mystery'
  }, {
    id: 'detective',
    name: 'Detective'
  }, {
    id: 'police-procedural',
    name: 'Police Procedural'
  }, {
    id: 'noir',
    name: 'Noir'
  }]
}, {
  id: 'thriller',
  name: 'Thriller',
  subgenres: [{
    id: 'psychological-thriller',
    name: 'Psychological Thriller'
  }, {
    id: 'legal-thriller',
    name: 'Legal Thriller'
  }, {
    id: 'medical-thriller',
    name: 'Medical Thriller'
  }, {
    id: 'political-thriller',
    name: 'Political Thriller'
  }]
}, {
  id: 'horror',
  name: 'Horror',
  subgenres: [{
    id: 'supernatural-horror',
    name: 'Supernatural Horror'
  }, {
    id: 'psychological-horror',
    name: 'Psychological Horror'
  }, {
    id: 'gothic-horror',
    name: 'Gothic Horror'
  }, {
    id: 'cosmic-horror',
    name: 'Cosmic Horror'
  }]
}, {
  id: 'historical-fiction',
  name: 'Historical Fiction',
  subgenres: [{
    id: 'ancient-history',
    name: 'Ancient History'
  }, {
    id: 'medieval',
    name: 'Medieval'
  }, {
    id: 'renaissance',
    name: 'Renaissance'
  }, {
    id: 'world-war',
    name: 'World War'
  }]
}, {
  id: 'contemporary',
  name: 'Contemporary Fiction',
  subgenres: [{
    id: 'literary-fiction',
    name: 'Literary Fiction'
  }, {
    id: 'womens-fiction',
    name: "Women's Fiction"
  }, {
    id: 'family-saga',
    name: 'Family Saga'
  }]
}];
// Spice level data
const spiceLevels = [{
  id: 'mild',
  name: 'Mild',
  description: 'No explicit content'
}, {
  id: 'warm',
  name: 'Warm',
  description: 'Kissing and mild references'
}, {
  id: 'spicy',
  name: 'Spicy',
  description: 'Some explicit scenes'
}, {
  id: 'extra-hot',
  name: 'Extra Hot',
  description: 'Explicit scenes'
}, {
  id: 'inferno',
  name: 'Inferno',
  description: 'Very explicit content'
}];
// Darkness level data
const darknessLevels = [{
  id: 'shadow',
  name: 'Shadow',
  description: 'No dark themes'
}, {
  id: 'grey',
  name: 'Grey',
  description: 'Some dark themes'
}, {
  id: 'abyss',
  name: 'Abyss/Dark',
  description: 'Dark themes throughout'
}, {
  id: 'pitch-black',
  name: 'Pitch Black',
  description: 'Very dark content'
}, {
  id: 'void',
  name: 'The Void',
  description: 'Extremely dark content'
}];
// Relationship types
const relationshipTypes = [{
  id: 'mf',
  name: 'MF',
  description: 'Male/Female'
}, {
  id: 'mm',
  name: 'MM',
  description: 'Male/Male'
}, {
  id: 'ff',
  name: 'FF',
  description: 'Female/Female'
}, {
  id: 'poly',
  name: 'Poly',
  description: 'Polyamorous'
}, {
  id: 'why-choose',
  name: 'Why Choose',
  description: 'Multiple love interests'
}];
// Ending types
const endingTypes = [{
  id: 'hea',
  name: 'HEA',
  description: 'Happily Ever After'
}, {
  id: 'hfn',
  name: 'HFN',
  description: 'Happy For Now'
}, {
  id: 'cliffhanger',
  name: 'Cliffhanger',
  description: 'Unresolved ending'
}, {
  id: 'bittersweet',
  name: 'Bittersweet',
  description: 'Mixed happiness and sadness'
}, {
  id: 'sad',
  name: 'Sad',
  description: 'Unhappy ending'
}, {
  id: 'tragic',
  name: 'Tragic',
  description: 'Tragic ending'
}];
type ViewMode = 'grid' | 'list';
export default function Browse() {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const {
    filterContent,
    platformData
  } = useAfterTideFilter();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'books';
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Saved reading preferences. Read-only here — browsing never writes back to
  // them, so temporary filter changes can't silently rewrite what's saved.
  const {
    preferenceFilters,
    hasPreferences
  } = useReadingPreferences();

  // Selected filters state, SEEDED from saved preferences so they're visibly
  // applied the moment Browse opens.
  const [selectedGenres, setSelectedGenres] = useState<string[]>(preferenceFilters.genres);
  const [selectedSpiceLevels, setSelectedSpiceLevels] = useState<string[]>(preferenceFilters.spiceLevels);
  const [selectedDarknessLevels, setSelectedDarknessLevels] = useState<string[]>(preferenceFilters.darknessLevels);
  const [selectedRelationshipTypes, setSelectedRelationshipTypes] = useState<string[]>([]);
  const [selectedEndingTypes, setSelectedEndingTypes] = useState<string[]>(preferenceFilters.endingTypes);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedTropes, setSelectedTropes] = useState<string[]>(preferenceFilters.tropes);
  const [excludedWarnings, setExcludedWarnings] = useState<string[]>(preferenceFilters.excludedWarnings);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // Use platform-appropriate data
  const baseBooks = platformData(allBooks, aftertideBooks);

  /**
   * Existing filter behaviour, unchanged, plus content-warning exclusion.
   * Extracted into a function so it can be run against a single book OR
   * against each edition of a linked story in turn.
   */
  const matchesFilters = (book: any) => {
    const matchesSearch = book.title.toLowerCase().includes(searchQuery.toLowerCase()) || book.author.toLowerCase().includes(searchQuery.toLowerCase());
    // Filter options are kebab-case slugs while book records hold Title Case
    // names and numeric levels, so every comparison goes through the shared
    // vocabulary bridge rather than comparing mismatched values directly.
    const matchesGenres = matchesGenreSlugs(book.genres, selectedGenres);
    const matchesFormats = selectedFormats.length === 0 || selectedFormats.some(format => (book.formats || []).includes(format));
    const matchesSpice = matchesLevelSlugs(book.spiceLevel, selectedSpiceLevels, spiceSlugToLevel);
    const matchesDarkness = matchesLevelSlugs(book.darknessLevel, selectedDarknessLevels, darknessSlugToLevel);
    const matchesRelationship = matchesRelationshipSlugs(book, selectedRelationshipTypes);
    const matchesEnding = matchesEndingSlugs(book.endingType ?? book.ending, selectedEndingTypes);
    const matchesTropes = matchesTagSlugs([...(book.tropes || []), ...(book.tags || [])], selectedTropes);
    // Exclusion, not selection: a book is dropped if it carries ANY warning
    // the reader has excluded.
    const matchesWarnings = !hasExcludedWarning(book.contentWarnings, excludedWarnings);
    return matchesSearch && matchesGenres && matchesFormats && matchesSpice && matchesDarkness && matchesRelationship && matchesEnding && matchesTropes && matchesWarnings;
  };

  /**
   * Edition-aware results.
   *
   * For a normal book this behaves exactly as before. For a story with linked
   * editions, each edition is tested against the filters on its OWN metadata —
   * so excluding "Graphic Violence" still surfaces the Light Edition while the
   * Dark Edition drops out. The story is then returned ONCE, carrying the
   * edition that matched, rather than appearing as two separate listings.
   */
  const filteredResults = baseBooks.flatMap((book: any) => {
    const story = getLinkedStoryForBook(book.id);
    if (!story) {
      return matchesFilters(book) ? [{
        book,
        matchedEdition: undefined
      }] : [];
    }
    const matching = story.editions.filter(edition => matchesFilters(applyEditionToBook(book, edition)));
    if (matching.length === 0) return [];
    // Prefer the default edition when more than one qualifies, so an
    // unfiltered browse shows the author's intended primary version.
    const preferred = matching.find(edition => edition.isDefault) || matching[0];
    return [{
      book: applyEditionToBook(book, preferred),
      matchedEdition: preferred,
      matchedCount: matching.length,
      totalEditions: story.editions.length
    }];
  });
  const filteredBooks = filteredResults.map(result => result.book);
  // Refs for carousel scrolling
  const carouselRefs = {
    bestSellers: useRef<HTMLDivElement>(null),
    trending: useRef<HTMLDivElement>(null),
    newAndNoteworthy: useRef<HTMLDivElement>(null),
    debutAuthors: useRef<HTMLDivElement>(null),
    readerFavorites: useRef<HTMLDivElement>(null)
  };
  const scrollCarousel = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) {
      const scrollAmount = 300;
      ref.current.scrollTo({
        left: ref.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount),
        behavior: 'smooth'
      });
    }
  };
  /** Restores saved preferences, discarding this session's manual changes. */
  const resetToPreferences = () => {
    setSelectedGenres(preferenceFilters.genres);
    setSelectedSpiceLevels(preferenceFilters.spiceLevels);
    setSelectedDarknessLevels(preferenceFilters.darknessLevels);
    setSelectedEndingTypes(preferenceFilters.endingTypes);
    setSelectedTropes(preferenceFilters.tropes);
    setExcludedWarnings(preferenceFilters.excludedWarnings);
    setSelectedRelationshipTypes([]);
    setSelectedFormats([]);
    setSearchQuery('');
  };

  /**
   * Clearing filters returns to saved preferences when the reader has any,
   * and to the empty default state otherwise — clearing should return you to
   * your own baseline, not strip the preferences you deliberately saved.
   */
  const handleReset = () => {
    if (hasPreferences) {
      resetToPreferences();
      return;
    }
    setSelectedGenres([]);
    setSelectedSpiceLevels([]);
    setSelectedDarknessLevels([]);
    setSelectedRelationshipTypes([]);
    setSelectedEndingTypes([]);
    setSelectedFormats([]);
    setSelectedTropes([]);
    setExcludedWarnings([]);
    setSearchQuery('');
  };

  // ── Preference vs session provenance ───────────────────────────────
  // Every active filter value is one of two things: applied from saved
  // preferences, or changed during this session. Readers need to tell them
  // apart, so provenance is computed per value rather than per page.
  const sameSelection = (a: string[], b: string[]) => a.length === b.length && a.every(value => b.includes(value));
  const matchesSavedPreferences = sameSelection(selectedGenres, preferenceFilters.genres) && sameSelection(selectedSpiceLevels, preferenceFilters.spiceLevels) && sameSelection(selectedDarknessLevels, preferenceFilters.darknessLevels) && sameSelection(selectedEndingTypes, preferenceFilters.endingTypes) && sameSelection(selectedTropes, preferenceFilters.tropes) && sameSelection(excludedWarnings, preferenceFilters.excludedWarnings) && selectedRelationshipTypes.length === 0 && selectedFormats.length === 0 && searchQuery === '';
  const activeFilterChips: Array<{
    key: string;
    label: string;
    fromPreferences: boolean;
    onRemove: () => void;
  }> = [...selectedGenres.map(value => ({
    key: `genre-${value}`,
    label: value.replace(/-/g, ' '),
    fromPreferences: preferenceFilters.genres.includes(value),
    onRemove: () => setSelectedGenres(prev => prev.filter(v => v !== value))
  })), ...selectedTropes.map(value => ({
    key: `trope-${value}`,
    label: value.replace(/-/g, ' '),
    fromPreferences: preferenceFilters.tropes.includes(value),
    onRemove: () => setSelectedTropes(prev => prev.filter(v => v !== value))
  })), ...selectedSpiceLevels.map(value => ({
    key: `spice-${value}`,
    label: `Spice: ${value.replace(/-/g, ' ')}`,
    fromPreferences: preferenceFilters.spiceLevels.includes(value),
    onRemove: () => setSelectedSpiceLevels(prev => prev.filter(v => v !== value))
  })), ...selectedDarknessLevels.map(value => ({
    key: `darkness-${value}`,
    label: `Darkness: ${value.replace(/-/g, ' ')}`,
    fromPreferences: preferenceFilters.darknessLevels.includes(value),
    onRemove: () => setSelectedDarknessLevels(prev => prev.filter(v => v !== value))
  })), ...selectedEndingTypes.map(value => ({
    key: `ending-${value}`,
    label: `Ending: ${value.toUpperCase()}`,
    fromPreferences: preferenceFilters.endingTypes.includes(value),
    onRemove: () => setSelectedEndingTypes(prev => prev.filter(v => v !== value))
  })), ...excludedWarnings.map(value => ({
    key: `exclude-${value}`,
    label: `Excluding: ${value}`,
    fromPreferences: preferenceFilters.excludedWarnings.includes(value),
    onRemove: () => setExcludedWarnings(prev => prev.filter(v => v !== value))
  }))];
  const filterSections: FilterSection[] = [{
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'romance',
      label: 'Romance'
    }, {
      value: 'fantasy',
      label: 'Fantasy'
    }, {
      value: 'sci-fi',
      label: 'Science Fiction'
    }, {
      value: 'mystery',
      label: 'Mystery'
    }, {
      value: 'thriller',
      label: 'Thriller'
    }, {
      value: 'horror',
      label: 'Horror'
    }, {
      value: 'historical-fiction',
      label: 'Historical Fiction'
    }, {
      value: 'contemporary',
      label: 'Contemporary Fiction'
    }],
    value: selectedGenres,
    onChange: (val: string) => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'format',
    title: 'Format',
    type: 'checkbox',
    options: [{
      value: 'ebook',
      label: 'eBook'
    }, {
      value: 'paperback',
      label: 'Paperback'
    }, {
      value: 'hardcover',
      label: 'Hardcover'
    }, {
      value: 'audiobook',
      label: 'Audiobook'
    }],
    value: selectedFormats,
    onChange: (val: string) => setSelectedFormats(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'spice',
    title: 'Spice Level',
    type: 'checkbox',
    options: [{
      value: 'mild',
      label: 'Mild'
    }, {
      value: 'warm',
      label: 'Warm'
    }, {
      value: 'spicy',
      label: 'Spicy'
    }, {
      value: 'extra-hot',
      label: 'Extra Hot'
    }, {
      value: 'inferno',
      label: 'Inferno'
    }],
    value: selectedSpiceLevels,
    onChange: (val: string) => setSelectedSpiceLevels(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'darkness',
    title: 'Darkness Level',
    type: 'checkbox',
    options: [{
      value: 'shadow',
      label: 'Shadow'
    }, {
      value: 'grey',
      label: 'Grey'
    }, {
      value: 'abyss',
      label: 'Abyss / Dark'
    }, {
      value: 'pitch-black',
      label: 'Pitch Black'
    }, {
      value: 'void',
      label: 'The Void'
    }],
    value: selectedDarknessLevels,
    onChange: (val: string) => setSelectedDarknessLevels(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'relationship',
    title: 'Relationship Types',
    type: 'checkbox',
    options: [{
      value: 'mf',
      label: 'MF'
    }, {
      value: 'mm',
      label: 'MM'
    }, {
      value: 'ff',
      label: 'FF'
    }, {
      value: 'poly',
      label: 'Poly'
    }, {
      value: 'why-choose',
      label: 'Why Choose'
    }],
    value: selectedRelationshipTypes,
    onChange: (val: string) => setSelectedRelationshipTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'ending',
    title: 'Ending Type',
    type: 'checkbox',
    options: [{
      value: 'hea',
      label: 'HEA'
    }, {
      value: 'hfn',
      label: 'HFN'
    }, {
      value: 'cliffhanger',
      label: 'Cliffhanger'
    }, {
      value: 'bittersweet',
      label: 'Bittersweet'
    }, {
      value: 'sad',
      label: 'Sad'
    }, {
      value: 'tragic',
      label: 'Tragic'
    }],
    value: selectedEndingTypes,
    onChange: (val: string) => setSelectedEndingTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'tropes',
    title: 'Tropes',
    type: 'checkbox',
    options: [{
      value: 'enemies-to-lovers',
      label: 'Enemies to Lovers'
    }, {
      value: 'found-family',
      label: 'Found Family'
    }, {
      value: 'age-gap',
      label: 'Age Gap'
    }, {
      value: 'forced-proximity',
      label: 'Forced Proximity'
    }, {
      value: 'fake-dating',
      label: 'Fake Dating'
    }, {
      value: 'second-chance',
      label: 'Second Chance'
    }, {
      value: 'slow-burn',
      label: 'Slow Burn'
    }, {
      value: 'forbidden-love',
      label: 'Forbidden Love'
    }, {
      value: 'friends-to-lovers',
      label: 'Friends to Lovers'
    }, {
      value: 'grumpy-sunshine',
      label: 'Grumpy / Sunshine'
    }],
    value: selectedTropes,
    onChange: (val: string) => setSelectedTropes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    // Exclusion rather than selection — ticking an item REMOVES books
    // carrying it. This is what makes linked editions demonstrable: a story
    // whose Dark Edition is excluded can still surface via its Light Edition.
    id: 'exclude-warnings',
    title: 'Exclude Content',
    type: 'checkbox',
    options: [{
      value: 'Graphic Violence',
      label: 'Graphic Violence'
    }, {
      value: 'Character Death',
      label: 'Character Death'
    }, {
      value: 'Revenge',
      label: 'Revenge'
    }, {
      value: 'Grief',
      label: 'Grief'
    }],
    value: excludedWarnings,
    onChange: (val: string) => setExcludedWarnings(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  // Clean up body overflow when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  const renderBookCarousel = (title: string, books: typeof bookSections.bestSellers, refKey: keyof typeof carouselRefs) => <div className="mb-6 md:mb-8 relative" data-id="element-8002" data-mp="12jjab3515b9a">
      <div className="flex justify-between items-center mb-3 md:mb-4" data-id="element-8003" data-mp="12ihw4pspw8nc">
        <h2 className={`text-lg md:text-xl font-bold ${dk.textHeading}`} data-id="element-8004" data-mp="okb8e2hhfp1e">
          {title}
        </h2>
        <Link to={`/category/${title.toLowerCase().replace(/\s+/g, '-')}`} className={`text-sm font-medium flex items-center transition-colors ${rt.accentLink}`} data-id="element-8005" data-mp="ticrgnewrocv">
          See More <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-8006" data-mp="jnb4ny83uydg" />
        </Link>
      </div>
      <div className="relative" data-id="element-8007" data-mp="5vmqzqi7k1ud">
        <button onClick={() => scrollCarousel(carouselRefs[refKey], 'left')} className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-1.5 rounded-full shadow-md transition-all ${dk.isDark ? 'bg-gray-700/90 text-gray-200 hover:bg-gray-600' : 'bg-white/80 text-gray-800 hover:bg-white'}`} aria-label="Scroll left" data-id="element-8008" data-mp="5s8pekhdbwnz">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-8009" data-mp="3ue0nsuc76wl3" />
        </button>
        <div ref={carouselRefs[refKey]} className="flex overflow-x-auto pb-4 hide-scrollbar gap-3 sm:gap-4 snap-x px-2" data-id="element-8010" data-mp="364pv1xei0sq">
          {books.map(book => <div key={book.id} className="flex-none w-36 sm:w-40 md:w-48 snap-start" data-id="element-8011" data-mp="c72u9lbr4abu">
              <UnifiedBookCard book={book} data-id="element-8012" data-mp="h6ywbf0w1ror" />
            </div>)}
        </div>
        <button onClick={() => scrollCarousel(carouselRefs[refKey], 'right')} className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-1.5 rounded-full shadow-md transition-all ${dk.isDark ? 'bg-gray-700/90 text-gray-200 hover:bg-gray-600' : 'bg-white/80 text-gray-800 hover:bg-white'}`} aria-label="Scroll right" data-id="element-8013" data-mp="m4qnu7xmfbsf">
          <ChevronRightIcon className="h-5 w-5" data-id="element-8014" data-mp="zuu3uh5wq99f" />
        </button>
      </div>
    </div>;
  return <div className={`min-h-screen ${dk.page}`} data-id="element-8015" data-mp="14tf4ghp2muye">
      {/* Page Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-8016" data-mp="1f0zwovq66e3y">
        <div className="max-w-7xl mx-auto" data-id="element-8017" data-mp="1a02gupsmtw5m">
          <h1 className={`text-2xl sm:text-3xl font-bold ${dk.textHeading}`} data-id="element-8018" data-mp="19z0uvqa2vp94">
            {type === 'merch' ? 'Literary Merchandise' : 'Discover Books'}
          </h1>
          <p className={`mt-2 text-sm sm:text-base ${dk.textSecondary}`} data-id="element-8019" data-mp="110p6luwop3ju">
            {type === 'merch' ? 'Discover official and fan-made merchandise inspired by your favorite books.' : 'Find your next favorite read from our curated collection of indie books.'}
          </p>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex max-w-7xl mx-auto w-full" data-id="element-8020" data-mp="1ny0hythxzw36">
        {/* Sidebar */}
        <FilterSidebarWrapper isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-8021" data-mp="1jyjbbwqs4swf">
          <FilterSidebar sections={filterSections} onReset={handleReset} searchable={true} data-id="element-8022" data-mp="9rvo0q46hum8" />
        </FilterSidebarWrapper>

        {/* Main content area */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6" data-id="element-8023" data-mp="35gyzua7ggd">
          {/* Toolbar row */}
          <div className="flex items-center justify-between mb-6" data-id="element-8024" data-mp="516vd7grirli">
            {/* Mobile filter toggle */}
            <button onClick={() => setIsSidebarOpen(true)} className={`lg:hidden flex items-center px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${dk.card} ${dk.textPrimary} ${dk.border}`} data-id="element-8025" data-mp="3n7214hf6nrj2">
              Filters
            </button>
            <div className="ml-auto" data-id="element-8026" data-mp="3i7fdde1qasw0">
              <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} data-id="element-8027" data-mp="3i8h1ok7r9bvo" />
            </div>
          </div>

          {type === 'books' ? <div className="space-y-6 md:space-y-8" data-id="element-8028" data-mp="1gglscb8z1jgm">
              <section className="space-y-6" data-id="element-8029" data-mp="1gg2mvu0icnjh">
                {renderBookCarousel('Best Sellers', bookSections.bestSellers, 'bestSellers')}
                {renderBookCarousel('Trending Now', bookSections.trending, 'trending')}
                {renderBookCarousel('New & Noteworthy', bookSections.newAndNoteworthy, 'newAndNoteworthy')}
                {renderBookCarousel('Debut Indie Authors', bookSections.debutAuthors, 'debutAuthors')}
                {renderBookCarousel('Reader Favorites', bookSections.readerFavorites, 'readerFavorites')}
              </section>

              <section className={`pt-2 md:pt-4 border-t ${dk.border}`} data-id="element-8030" data-mp="1uag0og9xg9yd">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-id="element-8031" data-mp="1uckcrlhzcf9q">
                  <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-8032" data-mp="1vrdampjsw5ge">
                    All Books
                  </h2>
                  {hasPreferences && <div className="flex items-center gap-3" data-id="element-8033" data-mp="25quer2jemba9">
                      <span className={`inline-flex items-center gap-1.5 text-xs ${dk.textSecondary}`} data-id="element-8034" data-mp="2aovupelj9uhi">
                        <SlidersHorizontalIcon className={`h-3.5 w-3.5 ${rt.textPrimary}`} data-id="element-8035" data-mp="1d2giar7yrn0t" />
                        {matchesSavedPreferences ? 'Filtered by your reading preferences' : 'Filters changed for this session'}
                      </span>
                      {!matchesSavedPreferences && <button onClick={resetToPreferences} className={`inline-flex items-center gap-1 text-xs font-medium ${rt.accentLink}`} data-id="element-8036" data-mp="ua4dzyxr2bo1">
                          <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-8037" data-mp="3chw68xn539wl" />
                          Reset to My Preferences
                        </button>}
                    </div>}
                </div>

                {/* Active filters, with provenance. A reader can see at a
                    glance which selections came from their saved preferences
                    and which they changed just now. */}
                {activeFilterChips.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-5" data-id="element-8038" data-mp="2osl4bcu1z9gv">
                    {activeFilterChips.map(chip => <button key={chip.key} onClick={chip.onRemove} title={chip.fromPreferences ? 'From your saved reading preferences — removing this only affects this session' : 'Changed during this browsing session'} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs capitalize border transition-colors ${chip.fromPreferences ? `${rt.bgLight} ${rt.textPrimary} ${rt.borderActive}` : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border}`}`} data-id="element-8039" data-mp="2fu9fngcqylr2">
                        {chip.fromPreferences && <BookmarkIcon className="h-3 w-3" data-id="element-8040" data-mp="2jzmpe4st4mkw" />}
                        {chip.label}
                        <XIcon className="h-3 w-3 opacity-60" data-id="element-8041" data-mp="2a7h0x51rknag" />
                      </button>)}
                    <span className={`text-xs ml-1 ${dk.textMuted}`} data-id="element-8042" data-mp="2xwrxsbfztxjb">
                      {activeFilterChips.filter(chip => chip.fromPreferences).length}{' '}
                      from preferences ·{' '}
                      {activeFilterChips.filter(chip => !chip.fromPreferences).length}{' '}
                      this session
                    </span>
                  </div>}
                <div className={`grid gap-3 sm:gap-4 md:gap-6 ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5' : 'grid-cols-1'}`} data-id="element-8043" data-mp="89x334gkrwg5">
                  {filteredResults.map(result => <div key={result.book.id} data-id="element-8044" data-mp="i9xmsricwekv">
                      <UnifiedBookCard book={result.book} data-id="element-8045" data-mp="d9jer9gr94mu" />
                      {/* Identifies WHICH edition matched, so a reader who
                          filtered on content knows what they're looking at
                          without opening the book. */}
                      {result.matchedEdition && <p className={`mt-1.5 text-xs flex items-start gap-1 ${dk.textSecondary}`} data-id="element-8046" data-mp="3i7xc19f5g30r">
                          <LayersIcon className={`h-3 w-3 mt-0.5 flex-shrink-0 ${rt.textPrimary}`} data-id="element-8047" data-mp="3n3w3x6zos35x" />
                          <span data-id="element-8048" data-mp="rpei7evzym0">
                            <span className="font-medium" data-id="element-8049" data-mp="sqsajnaez7f">
                              {result.matchedEdition.name}
                            </span>
                            {result.matchedCount && result.totalEditions && result.matchedCount < result.totalEditions ? ` matches your filters (${result.matchedCount} of ${result.totalEditions} editions)` : ` · ${result.totalEditions} editions available`}
                          </span>
                        </p>}
                    </div>)}
                </div>
              </section>
            </div> : <div className={`grid gap-4 sm:gap-6 ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4' : 'grid-cols-1'}`} data-id="element-8050" data-mp="296xbpyhhq6e2">
              {merchandise.map(product => <ProductCard key={product.id} {...product} data-id="element-8051" data-mp="1z8zmb7nwa3jv" />)}
            </div>}
        </main>
      </div>

      <style jsx global data-id="element-8052" data-mp="2wuwxsl3ux8mb">{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
}